/*
 * @Author: lizhigang
 * @Date: 2025-10-21 11:05:25
 * @Company: www.smm.cn
 * @Description: 登录
 */
"use client"

import "./index.scss"

import IconFont from "@repo/ui/iconfont"
import { useLocale, useTranslations } from "next-intl"
import { useSearchParams } from "next/navigation"
import {
  AccountInput,
  composeCellphone,
  cycleFormInputs,
  normalizePhoneNumber,
  resolveAccountType,
  type AccountInputValue,
} from "smm-auth-widget"
import { useGoogleLogin } from "smm-auth-widget/hooks/useGoogleLogin"
import { useLogin, type AuthResult } from "smm-auth-widget/ui"
import { useCallback, useEffect, useState, type ReactNode } from "react"
import { Button, Checkbox, Divider, Form, Input, message } from "antd"

import { usePasswordInput } from "../../_hooks"
import { SIGN_IN_AUTH_CONFIG, SIGN_IN_EN_H5_SOURCE_MAP, SIGN_IN_EN_WEB_SOURCE_MAP } from "../../const"
import { isMobile } from "../../utils"
import { AuthConfigProvider } from "../AuthConfigProvider"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  language?: string
  onLogined?: (msg?: string) => void
  handleSignUp: () => void
  handleForgot: () => void
}

interface LoginErrorLimitData {
  account_error_count: number
  count_down: number
  error_limit: number
}

interface LoginOriginResponse {
  code?: number
  data?: unknown | null
  msg?: string
}

const GOOGLE_RUNTIME_ERROR_PREFIXES = [
  "Load Google script failed",
  "Google API not loaded",
  "Google auth2 init failed",
  "Google sign in failed:",
]

const shouldShowGoogleApiToast = (error: string) => {
  return !GOOGLE_RUNTIME_ERROR_PREFIXES.some((prefix) => error.startsWith(prefix))
}

const Login = ({ language, onLogined, handleSignUp, handleForgot, ...attr }: Props) => {
  const locale = useLocale()
  const t = useTranslations()
  const searchParams = useSearchParams()
  const urlParam = searchParams.get("url")
  const [form] = Form.useForm()
  const values = Form.useWatch([], form)
  const [account, setAccount] = useState<AccountInputValue>({ account: "", phoneCode: "" })
  const [accountHasError, setAccountHasError] = useState(true)
  const [password, setPassword] = useState("")
  const [loading, loginApi] = useLogin()
  const [submittable, setSubmittable] = useState(false)
  const [messageApi, contextHolder] = message.useMessage({
    maxCount: 1,
    getContainer: () => document.querySelector("#action") || document.body,
  })

  // 使用密码输入 Hook
  const { handlePasswordChange, handlePasswordPaste, handleCompositionStart } = usePasswordInput({
    form,
    fieldName: "password",
    onPasswordChange: setPassword,
  })

  const currentLocale = language || locale || "en"
  const accountType = resolveAccountType(account.account, "auto")
  const userName = accountType === "phone" ? composeCellphone(account.phoneCode, account.account) : account.account
  const buildProfileCompleteUrl = (phone?: { phoneCode: string; phoneNumber: string }) => {
    const params = new URLSearchParams()
    if (urlParam) params.set("url", urlParam)
    if (phone) {
      params.set("phoneCode", phone.phoneCode)
      params.set("phoneNumber", phone.phoneNumber)
    }
    const query = params.toString()
    return `${process.env.NEXT_PUBLIC_API_HOST_USER}/${currentLocale}/profile-complete${query ? `?${query}` : ""}`
  }
  const PROFILE_COMPLETE_URL = buildProfileCompleteUrl()
  const getForgotUrl = () => {
    if (typeof window === "undefined") {
      return `${process.env.NEXT_PUBLIC_API_HOST_USER}/${currentLocale}/forgot`
    }

    const currentUrl = new URL(window.location.href)
    const redirectUrl = currentUrl.searchParams.get("url") || currentUrl.href

    return `${process.env.NEXT_PUBLIC_API_HOST_USER}/${currentLocale}/forgot?url=${encodeURIComponent(redirectUrl)}`
  }

  // 使用 Google 登录 hook
  const { googleLoginRef, GoogleScript } = useGoogleLogin({
    onSuccess: (msg) => {
      onLogined?.(msg)
    },
    locale: currentLocale,
    profileCompleteUrl: PROFILE_COMPLETE_URL,
    config: SIGN_IN_AUTH_CONFIG,
    onError: (error) => {
      if (shouldShowGoogleApiToast(error)) {
        message.error({
          className: "sign-in-api-toast",
          content: error || "unknown error",
        })
      }
    },
  })

  const getLoginErrorMessage = (response: LoginOriginResponse): string | ReactNode => {
    if (!response?.data) {
      throw new Error(response?.msg)
    }
    const { error_limit, account_error_count } = response.data as LoginErrorLimitData
    const remaining = error_limit - account_error_count

    if (response?.code === 10017 || remaining === 0) {
      messageApi.error(
        t.rich("login.signIn_locked_toast", {
          link: (chunks) => (
            <a style={{ textDecoration: "underline" }} href={getForgotUrl()}>
              {chunks}
            </a>
          ),
        })
      )
      return
    }

    if (remaining < 6 && remaining > 0) {
      messageApi.error(t("login.signIn_toast_with_tries_left", { count: remaining }))
      return
    }

    throw new Error(response?.msg)
  }

  /**
   * 登录
   */
  const handleSubmit = async () => {
    if (loading || accountHasError || !password.trim()) {
      return
    }
    const values = await form.validateFields()
    if (!values) return

    await loginApi({
      config: SIGN_IN_AUTH_CONFIG,
      values: {
        email: userName,
        password: values.password,
        remember: Boolean(values.remember),
        locale,
        returnUrl: urlParam || window.location.href,
        source: isMobile()
          ? (SIGN_IN_EN_H5_SOURCE_MAP[window.location.host] ?? "")
          : (SIGN_IN_EN_WEB_SOURCE_MAP[window.location.host] ?? ""),
      },
      profileCompleteUrl:
        accountType === "phone"
          ? buildProfileCompleteUrl({
              phoneCode: normalizePhoneNumber(account.phoneCode),
              phoneNumber: normalizePhoneNumber(account.account),
            })
          : PROFILE_COMPLETE_URL,
      onSuccess: (response) => {
        onLogined?.(response.msg)
      },
      onError: (response: AuthResult) => {
        if (response.code === 20004) {
          messageApi.open({
            type: "warning",
            duration: 5,
            content: (
              <div>
                {t.rich("login.simplePasswordMessage", {
                  link: (chunks) => (
                    <a style={{ textDecoration: "underline" }} href={getForgotUrl()}>
                      {chunks}
                    </a>
                  ),
                })}
              </div>
            ),
          })
          return
        }

        try {
          getLoginErrorMessage(response as LoginOriginResponse)
        } catch (errorInfo) {
          const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
          messageApi.open({
            type: "error",
            content: msg || "unknown error",
          })
        }
      },
    })
  }

  const sync = useCallback(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false))
  }, [form])

  useEffect(() => {
    sync()
  }, [form, values, sync])

  useEffect(() => {
    // 页面重新可见
    document.addEventListener("visibilitychange", sync)
    window.addEventListener("focus", sync)

    return () => {
      document.removeEventListener("visibilitychange", sync)
      window.removeEventListener("focus", sync)
    }
  }, [sync])

  return (
    <AuthConfigProvider>
      {contextHolder}
      <div className="loginWrapper page" {...attr}>
        <div>
          <div className="logo">
            <IconFont type="icon-SMMlogo-daizimu" style={{ width: 80, height: 32, fontSize: 80 }} />
          </div>
          <h2 className="modalTitle page">{t("login.title")}</h2>
          <Form
            onKeyDown={cycleFormInputs}
            layout="vertical"
            form={form}
            variant="filled"
            autoComplete="off"
            className={"loginForm"}
            requiredMark={false}
          >
            <div className="accountInputWrapperClass">
              <Form.Item label={null}>
                <AccountInput
                  mode="auto"
                  locale={currentLocale}
                  value={account}
                  className="loginAccountInput"
                  placeholder={t("accountInput.accountPlaceholder")}
                  onChange={(value, hasError) => {
                    setAccount(value)
                    setAccountHasError(hasError)
                  }}
                  onEnter={() => void handleSubmit()}
                />
              </Form.Item>
            </div>
            <Form.Item name="passwordWrapper" className="password-wrapper" style={{ position: "relative" }}>
              <Form.Item
                name="password"
                label={null}
                normalize={(value) => value?.trim()}
                rules={[{ required: true, message: t("login.requirePassword") }]}
              >
                <Input.Password
                  placeholder={t("login.password")}
                  onChange={handlePasswordChange}
                  onPressEnter={handleSubmit}
                  onCompositionStart={handleCompositionStart}
                  onPaste={handlePasswordPaste}
                  allowClear
                />
              </Form.Item>
            </Form.Item>
            <Form.Item className="remember" name="remember" valuePropName="checked">
              <div>
                <Checkbox>{t("login.remember")}</Checkbox>
                <span className={"pageSubTip"} onClick={handleForgot}>
                  {t("login.forgot")}?
                </span>
              </div>
            </Form.Item>
            <Form.Item className="submitButton">
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={!submittable || accountHasError}
                loading={loading}
                block
                danger
              >
                {t("login.signIn")}
              </Button>
            </Form.Item>
          </Form>
          <div className={"bottomTip"}>
            {t("login.noAccount")}
            <a
              className={"text"}
              onClick={() => {
                handleSignUp()
              }}
            >
              {t("login.signUp")}
            </a>
          </div>
        </div>
        <div>
          <Divider className={`divider`}>{t("login.or")}</Divider>
          <div ref={googleLoginRef} className={"googleLogin"}>
            <IconFont type="icon-Google" width={30} height={30} style={{ marginRight: 8, fontSize: 30 }} />
            <span className="text">{t("login.continue")}</span>
          </div>
        </div>
        <GoogleScript />
      </div>
    </AuthConfigProvider>
  )
}

export default Login
